import { HeroUIProvider, type HeroUIProviderProps } from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { HelmetProvider, type HelmetServerState } from "react-helmet-async";
import { initVGroveClient, isVGroveClientInitialized, type VGroveClientConfig } from "../client";
import type { LayoutFeatures, LayoutSettings } from "../types";
import { ThemeProvider, type ThemeProviderProps as CustomThemeProviderProps } from "./ThemeProvider";
import { VGroveContext, type VGroveContextType } from "./VGroveContext";
import { debug } from '../client';
interface HelmetProviderProps {
    context?: {
        helmet?: HelmetServerState;
    };
}

export interface VGroveProviderProps {
    settings?: LayoutSettings;
    herouiConfig?: Omit<HeroUIProviderProps, 'children'>;
    helmetConfig?: HelmetProviderProps;
    themeConfig?: Omit<CustomThemeProviderProps, 'children'>;
    /** VGrove Client 配置 - 包含调试配置 */
    clientConfig?: VGroveClientConfig;
    /** 是否自动初始化 VGrove Client */
    autoInitClient?: boolean;
    /** 是否仅在未初始化时才初始化（默认为false，总是尝试初始化） */
    initOnlyOnce?: boolean;
    children: React.ReactNode;
}

// 默认布局设置
const defaultLayoutSettings: LayoutSettings = {
    sidebar: true,
    header: true,
    footer: true,
    navbar: {
        sticky: true,
        transparent: false,
        height: "64px"
    },
    sidebarConfig: {
        width: "280px",
        collapsible: true,
        defaultCollapsed: false
    },
    footerConfig: {
        sticky: false,
        height: "auto"
    },
    theme: {
        mode: "auto",
        primaryColor: "#006FEE"
    },
    responsive: {
        hideSidebarOnMobile: true,
        mobileBreakpoint: "768px"
    },
    debug: {
        enabled: false,
        auth: false,
        i18n: false,
        performance: false,
        routing: false,
        storage: false
    }
};

export const VGroveProvider: React.FC<VGroveProviderProps> = ({
    children,
    settings: initialSettings,
    herouiConfig,
    helmetConfig,
    themeConfig,
    clientConfig,
    autoInitClient = true,
    initOnlyOnce = false
}) => {
    const [settings, setSettings] = useState<LayoutSettings>(() => ({
        ...defaultLayoutSettings,
        ...initialSettings
    }));

    // 🎯 修复：使用单一状态管理布局状态，消除状态冗余
    // 🔥 临时修复：由于VGroveLayout组件渲染问题，先设置为ready状态
    const [layoutState, setLayoutState] = useState<'loading' | 'ready'>('ready');

    const updateSettings = useCallback((newSettings: Partial<LayoutSettings>) => {
        setSettings(prev => ({
            ...prev,
            ...newSettings,
            // 深度合并嵌套对象
            navbar: { ...prev.navbar, ...newSettings.navbar },
            sidebarConfig: { ...prev.sidebarConfig, ...newSettings.sidebarConfig },
            footerConfig: { ...prev.footerConfig, ...newSettings.footerConfig },
            theme: { ...prev.theme, ...newSettings.theme },
            responsive: { ...prev.responsive, ...newSettings.responsive },
            debug: { ...prev.debug, ...newSettings.debug },
        }));
    }, []);

    const resetSettings = useCallback(() => {
        setSettings(defaultLayoutSettings);
    }, []);

    // 🎯 修复：统一状态管理方法
    const setLayoutReady = useCallback((ready: boolean) => {
        debug.log('🔄 [VGroveProvider] 设置布局状态:', {
            state: ready ? 'ready' : 'loading',
            timestamp: new Date().toISOString()
        });
        setLayoutState(ready ? 'ready' : 'loading');
    }, []);

    // 🎯 修复：重置布局状态（用于路由切换）
    const resetLayoutState = useCallback(() => {
        debug.log('🔄 [VGroveProvider] 重置布局状态');
        setLayoutState('loading');
    }, []);

    // 自动初始化 VGrove Client
    useEffect(() => {
        if (!autoInitClient) return;

        // 如果设置了仅初始化一次，且已经初始化过了，则跳过
        if (initOnlyOnce && isVGroveClientInitialized()) {
            return;
        }

        const config: VGroveClientConfig = {
            // 合并来自布局设置的 debug 配置和直接传入的 clientConfig
            debug: {
                ...settings.debug,
                ...clientConfig?.debug
            },
            // 其他配置项
            ...clientConfig
        };

        try {
            initVGroveClient(config);
        } catch (error) {
            debug.error('❌ Failed to initialize VGrove Client:', error);
        }
    }, [autoInitClient, initOnlyOnce, settings.debug, clientConfig]);

    const features = useMemo((): LayoutFeatures => ({
        hasSidebar: settings.sidebar ?? true,
        hasHeader: settings.header ?? true,
        hasFooter: settings.footer ?? true,
        isSidebarCollapsible: settings.sidebarConfig?.collapsible ?? true,
        isNavbarSticky: settings.navbar?.sticky ?? true,
        isFooterSticky: settings.footerConfig?.sticky ?? false,
        shouldHideSidebarOnMobile: settings.responsive?.hideSidebarOnMobile ?? true,
    }), [settings]);

    const contextValue = useMemo((): VGroveContextType => ({
        settings,
        updateSettings,
        resetSettings,
        features,
        // 🎯 修复：派生状态，从单一状态源计算
        isLayoutLoading: layoutState === 'loading',
        isLayoutReady: layoutState === 'ready',
        setLayoutReady,
        resetLayoutState
    }), [settings, updateSettings, resetSettings, features, layoutState, setLayoutReady, resetLayoutState]);

    // 获取主题配置，将 VGrove 设置映射到 ThemeProvider 配置
    // 使用 useMemo 确保只在相关依赖变化时重新计算
    const themeProviderProps = useMemo((): Partial<CustomThemeProviderProps> => {
        // 基础配置 - 永远不传递 defaultTheme，让 ThemeProvider 自己处理
        const baseConfig: Partial<CustomThemeProviderProps> = {
            storageKey: "vgrove-theme",
            enableSystem: true,
            ...themeConfig
        };
        return baseConfig;
    }, [themeConfig]); // 移除对 initialSettings 的依赖

    return (
        <VGroveContext.Provider value={contextValue}>
            <HelmetProvider {...helmetConfig}>
                <HeroUIProvider {...herouiConfig}>
                    <ThemeProvider {...themeProviderProps}>
                        {children}
                    </ThemeProvider>
                </HeroUIProvider>
            </HelmetProvider>
        </VGroveContext.Provider>
    );
};